import React from 'react';
import { AwardedRoutine } from '../../types/awards';
import { getTemplateById } from '../../constants/awardTypeTemplates';
import { PrintSection, PrintColumn, defaultColumnFormatters } from './PrintTemplate';

// Layout factory for different award types
export class PrintLayoutFactory {
  private static getAgeDivisionOrder(ageGroup: string): number {
    const ageOrderMap: { [key: string]: number } = {
      'PeeWee': 1,
      'Mini': 2,
      'Junior': 3,
      'Teen': 4,
      'Senior': 5,
      'Open': 6,
      'Streetz Stars': 1,
    };
    return ageOrderMap[ageGroup] || 999;
  }

  private static getCategoryOrder(category: string): number {
    const categoryOrderMap: { [key: string]: number } = {
      'Solo': 1,
      'Duo': 2,
      'Trio': 2,
      'Duo/Trio': 2,
      'Small Group': 3,
      'Large Group': 4,
      'Line': 5,
      'Extended Line': 6,
      'Ext. Line': 6,
      'Production': 7
    };
    return categoryOrderMap[category] || 999;
  }

  // Helper function to get a simplified label for split columns
  private static getSplitColumnLabel(divisions: string[]): string {
    if (divisions.length === 0) return '';
    if (divisions.length === 1) return divisions[0];
    
    // Priority order for preferred labels when multiple divisions are present
    const preferredLabels = ['Large Group', 'Small Group', 'Line', 'Ext. Line', 'Extended Line'];
    
    // Check if any preferred label is in the divisions
    for (const preferred of preferredLabels) {
      if (divisions.includes(preferred)) {
        return preferred;
      }
    }
    
    // Fallback: join all divisions with commas
    return divisions.join(', ');
  }

  // Column definitions for different award types
  private static getColumnDefinitions(reportType: string): PrintColumn[] {
    const template = getTemplateById(reportType.split('-')[0]); // Handle variants like "highScoreByAge-10and5"
    
    if (!template) {
      // Fallback columns
      return [
        { key: 'routineId', label: '#', width: '60px', formatter: defaultColumnFormatters.routineNumber },
        { key: 'studio', label: 'Studio', width: '180px', formatter: defaultColumnFormatters.studioName },
        { key: 'routineName', label: 'Routine', width: '280px', formatter: defaultColumnFormatters.routineName },
        { key: 'award', label: 'Score', width: '140px', formatter: defaultColumnFormatters.score, className: 'score-cell' },
        { key: 'ageGroup', label: 'Age Division', width: '100px', formatter: defaultColumnFormatters.ageGroup },
        { key: 'category', label: 'Category', width: '100px', formatter: defaultColumnFormatters.category },
        { key: 'division', label: 'Performance Division', width: '140px', formatter: defaultColumnFormatters.division }
      ];
    }

    const columns: PrintColumn[] = [];

    // Build columns based on template configuration
    template.columns.forEach(columnName => {
      switch (columnName) {
        case 'Placement':
          columns.push({
            key: 'placement',
            label: 'Place',
            width: '50px',
            align: 'center',
            formatter: defaultColumnFormatters.placement
          });
          break;
        case '#':
          columns.push({
            key: 'routineId',
            label: '#',
            width: '60px',
            align: 'center',
            formatter: defaultColumnFormatters.routineNumber
          });
          break;
        case 'Studio':
        case 'Studio Name':
          columns.push({
            key: 'studio',
            label: 'Studio',
            width: template.showsStudioName ? '180px' : '120px',
            formatter: defaultColumnFormatters.studioName
          });
          break;
        case 'Routine':
          columns.push({
            key: 'routineName',
            label: 'Routine',
            width: '280px',
            formatter: defaultColumnFormatters.routineName,
            className: 'routine-title-cell'
          });
          break;
        case 'Score':
          columns.push({
            key: 'award',
            label: 'Score',
            width: '140px',
            formatter: defaultColumnFormatters.score,
            className: 'score-cell',
            printClassName: reportType === 'adjudication' ? 'text-red-600' : ''
          });
          break;
        case 'Age Division':
          columns.push({
            key: 'ageGroup',
            label: 'Age Division',
            width: '100px',
            formatter: defaultColumnFormatters.ageGroup
          });
          break;
        case 'Category':
          columns.push({
            key: 'category',
            label: 'Category',
            width: '100px',
            formatter: defaultColumnFormatters.category
          });
          break;
        case 'Performance Division':
          columns.push({
            key: 'division',
            label: 'Performance Division',
            width: '140px',
            formatter: defaultColumnFormatters.division
          });
          break;
      }
    });

    return columns;
  }

  // Create sections for adjudication awards
  static createAdjudicationLayout(routines: AwardedRoutine[]): PrintSection[] {
    const columns = this.getColumnDefinitions('adjudication');
    
    return [{
      id: 'adjudication-awards',
      title: 'Adjudication Awards',
      routines: routines,
      columns: columns,
      showHeader: false // Single table, no section header needed
    }];
  }

  // Create sections for high score by age
  static createHighScoreByAgeLayout(routines: AwardedRoutine[], reportType: string): PrintSection[] {
    const columns = this.getColumnDefinitions(reportType);
    const sections: PrintSection[] = [];

    // Group by age, then by category
    const groupedByAgeAndCategory: { [ageGroup: string]: { [category: string]: AwardedRoutine[] } } = {};
    
    routines.forEach(routine => {
      if (!groupedByAgeAndCategory[routine.ageGroup]) {
        groupedByAgeAndCategory[routine.ageGroup] = {};
      }
      if (!groupedByAgeAndCategory[routine.ageGroup][routine.category]) {
        groupedByAgeAndCategory[routine.ageGroup][routine.category] = [];
      }
      groupedByAgeAndCategory[routine.ageGroup][routine.category].push(routine);
    });

    // Sort age groups
    const sortedAgeGroups = Object.keys(groupedByAgeAndCategory).sort((a, b) => 
      this.getAgeDivisionOrder(a) - this.getAgeDivisionOrder(b)
    );

    sortedAgeGroups.forEach(ageGroup => {
      const sortedCategories = Object.keys(groupedByAgeAndCategory[ageGroup]).sort((a, b) => 
        this.getCategoryOrder(a) - this.getCategoryOrder(b)
      );

      sortedCategories.forEach(category => {
        sections.push({
          id: `age-category-${ageGroup}-${category}`,
          title: `${ageGroup} ${category} High Score`,
          routines: groupedByAgeAndCategory[ageGroup][category],
          columns: columns,
          showHeader: true,
          headerClassName: 'section-header-primary'
        });
      });
    });

    return sections;
  }

  // Create sections for high score by division
  static createHighScoreByDivisionLayout(routines: AwardedRoutine[], reportType: string): PrintSection[] {
    const columns = this.getColumnDefinitions(reportType);
    const sections: PrintSection[] = [];

    // Group by age, then by performance division
    const groupedByAgeAndDivision: { [ageGroup: string]: { [division: string]: AwardedRoutine[] } } = {};
    
    routines.forEach(routine => {
      if (!groupedByAgeAndDivision[routine.ageGroup]) {
        groupedByAgeAndDivision[routine.ageGroup] = {};
      }
      if (!groupedByAgeAndDivision[routine.ageGroup][routine.division]) {
        groupedByAgeAndDivision[routine.ageGroup][routine.division] = [];
      }
      groupedByAgeAndDivision[routine.ageGroup][routine.division].push(routine);
    });

    // Sort age groups
    const sortedAgeGroups = Object.keys(groupedByAgeAndDivision).sort((a, b) => 
      this.getAgeDivisionOrder(a) - this.getAgeDivisionOrder(b)
    );

    sortedAgeGroups.forEach(ageGroup => {
      const sortedDivisions = Object.keys(groupedByAgeAndDivision[ageGroup]).sort();

      sortedDivisions.forEach(division => {
        sections.push({
          id: `age-division-${ageGroup}-${division}`,
          title: `${ageGroup} ${division} High Score`,
          routines: groupedByAgeAndDivision[ageGroup][division],
          columns: columns,
          showHeader: true,
          headerClassName: 'section-header-primary'
        });
      });
    });

    return sections;
  }

  // Create sections for greatest hits
  static createGreatestHitsLayout(routines: AwardedRoutine[], customAwards: any[], reportType: string): PrintSection[] {
    const columns = this.getColumnDefinitions(reportType);
    const sections: PrintSection[] = [];

    // Get award display name and custom config
    const customAward = customAwards.find(award => {
      if (!award.enabled) return false;
      return reportType.startsWith(award.templateType);
    });
    const awardDisplayName = customAward?.displayName || 'Award';
    
    // Get split age groups configuration
    const splitAgeGroups = customAward?.config?.splitAgeGroups || {};

    // Group by age division
    const groupedByAge: { [ageGroup: string]: AwardedRoutine[] } = {};
    
    routines.forEach(routine => {
      const routineAgeGroup = routine.ageGroup;
      const routineDivision = routine.division;
      
      // If this age group is split, check if the division matches either column
      if (routineAgeGroup in splitAgeGroups) {
        const splitColumns = splitAgeGroups[routineAgeGroup];
        // splitColumns is an array of two arrays: [column1Divisions, column2Divisions]
        const column1Divisions = splitColumns[0] || [];
        const column2Divisions = splitColumns[1] || [];
        const allAllowedDivisions = [...column1Divisions, ...column2Divisions];
        
        // If divisions are specified for this split age group, filter by them
        if (allAllowedDivisions.length > 0 && !allAllowedDivisions.includes(routineDivision)) {
          return; // Skip this routine if division doesn't match split
        }
      }
      // If age group is not split, include all routines (no filtering needed)
      
      if (!groupedByAge[routineAgeGroup]) {
        groupedByAge[routineAgeGroup] = [];
      }
      groupedByAge[routineAgeGroup].push(routine);
    });

    // Sort age groups
    const sortedAgeGroups = Object.keys(groupedByAge).sort((a, b) => 
      this.getAgeDivisionOrder(a) - this.getAgeDivisionOrder(b)
    );

    sortedAgeGroups.forEach(ageGroup => {
      const ageGroupRoutines = groupedByAge[ageGroup];
      const splitColumns = splitAgeGroups[ageGroup];
      
      // Helper function to sort and create a section
      const createSection = (routines: AwardedRoutine[], title: string, id: string) => {
        if (routines.length === 0) return;
        
        const sortedRoutines = [...routines].sort((a, b) => {
          if (a.finalScore !== b.finalScore) {
            return b.finalScore - a.finalScore;
          }
          
          const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                            a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
          const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                            b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
          
          return bDropScore - aDropScore;
        });

        sections.push({
          id: id,
          title: title,
          routines: sortedRoutines,
          columns: columns,
          showHeader: true,
          headerClassName: 'section-header-secondary'
        });
      };
      
      // Check if this age group is split into two columns
      if (splitColumns && Array.isArray(splitColumns) && splitColumns.length >= 2) {
        const column1Divisions = splitColumns[0] || [];
        const column2Divisions = splitColumns[1] || [];
        
        // Filter routines for column 1
        const column1Routines = column1Divisions.length > 0
          ? ageGroupRoutines.filter(routine => column1Divisions.includes(routine.division))
          : [];
        
        // Filter routines for column 2
        const column2Routines = column2Divisions.length > 0
          ? ageGroupRoutines.filter(routine => column2Divisions.includes(routine.division))
          : [];
        
        // Create sections for both columns
        if (column1Routines.length > 0) {
          const column1Title = `${ageGroup} ${awardDisplayName} - ${this.getSplitColumnLabel(column1Divisions)}`;
          createSection(column1Routines, column1Title, `age-${ageGroup}-col1`);
        }
        
        if (column2Routines.length > 0) {
          const column2Title = `${ageGroup} ${awardDisplayName} - ${this.getSplitColumnLabel(column2Divisions)}`;
          createSection(column2Routines, column2Title, `age-${ageGroup}-col2`);
        }
        
        // If no routines in either column but age group is split, show all as fallback
        if (column1Routines.length === 0 && column2Routines.length === 0 && ageGroupRoutines.length > 0) {
          createSection(ageGroupRoutines, `${ageGroup} ${awardDisplayName}`, `age-${ageGroup}-fallback`);
        }
      } else {
        // Not split - show all routines for this age group
        createSection(ageGroupRoutines, `${ageGroup} ${awardDisplayName}`, `age-${ageGroup}`);
      }
    });

    return sections;
  }

  // Create sections for studio standouts
  static createStudioStandoutLayout(routines: AwardedRoutine[], customAwards: any[], reportType: string): PrintSection[] {
    const columns = this.getColumnDefinitions(reportType);

    // Get award display name
    const customAward = customAwards.find(award => {
      if (!award.enabled) return false;
      return reportType.startsWith(award.templateType);
    });
    const awardDisplayName = customAward?.displayName || 'Studio Standouts';

    // Sort all routines by studio name (alphabetically), then by score (highest to lowest)
    const sortedRoutines = [...routines].sort((a, b) => {
      // First compare studio names alphabetically
      const studioA = (a.studio || '').toLowerCase();
      const studioB = (b.studio || '').toLowerCase();
      if (studioA !== studioB) {
        return studioA.localeCompare(studioB);
      }
      
      // If same studio, compare final scores (highest to lowest)
      if (a.finalScore !== b.finalScore) {
        return b.finalScore - a.finalScore;
      }
      
      // Tie-breaker with drop scores
      const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                        a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
      const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                        b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
      
      return bDropScore - aDropScore;
    });

    return [{
      id: 'studio-standouts',
      title: awardDisplayName,
      routines: sortedRoutines,
      columns: columns,
      showHeader: true,
      headerClassName: 'section-header-secondary'
    }];
  }

  // Create sections for winners (manually selected)
  static createWinnersLayout(routines: AwardedRoutine[], customAwards: any[], reportType: string): PrintSection[] {
    // For winners, we want a clean layout without placement numbers since these are manually curated
    const columns = this.getWinnersColumnDefinitions(reportType);
    
    // Get award display name
    const customAward = customAwards.find(award => {
      if (!award.enabled) return false;
      return reportType.startsWith(award.templateType);
    });
    const awardDisplayName = customAward?.displayName || 'Award';

    if (reportType === 'greatestHits' || reportType.startsWith('greatestHits')) {
      // Get split age groups configuration
      const splitAgeGroups = customAward?.config?.splitAgeGroups || {};
      
      // Group by age division for Greatest Hits winners
      const groupedByAge: { [ageGroup: string]: AwardedRoutine[] } = {};
      
      routines.forEach(routine => {
        if (!groupedByAge[routine.ageGroup]) {
          groupedByAge[routine.ageGroup] = [];
        }
        groupedByAge[routine.ageGroup].push(routine);
      });

      // Sort age groups
      const sortedAgeGroups = Object.keys(groupedByAge).sort((a, b) => 
        this.getAgeDivisionOrder(a) - this.getAgeDivisionOrder(b)
      );

      const sections: PrintSection[] = [];
      
      // Helper function to sort and create a section
      const createSection = (routines: AwardedRoutine[], title: string, id: string) => {
        if (routines.length === 0) return;
        
        const sortedRoutines = [...routines].sort((a, b) => {
          if (a.finalScore !== b.finalScore) {
            return b.finalScore - a.finalScore;
          }
          
          // Tie-breaker with drop scores
          const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                            a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
          const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                            b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
          
          return bDropScore - aDropScore;
        });

        sections.push({
          id: id,
          title: title,
          routines: sortedRoutines,
          columns: columns,
          showHeader: true,
          headerClassName: 'section-header-winners'
        });
      };
      
      sortedAgeGroups.forEach(ageGroup => {
        const ageGroupRoutines = groupedByAge[ageGroup];
        const splitColumns = splitAgeGroups[ageGroup];
        
        // Check if this age group is split into two columns
        if (splitColumns && Array.isArray(splitColumns) && splitColumns.length >= 2) {
          const column1Divisions = splitColumns[0] || [];
          const column2Divisions = splitColumns[1] || [];
          
          // Filter routines for column 1
          const column1Routines = column1Divisions.length > 0
            ? ageGroupRoutines.filter(routine => column1Divisions.includes(routine.division))
            : [];
          
          // Filter routines for column 2
          const column2Routines = column2Divisions.length > 0
            ? ageGroupRoutines.filter(routine => column2Divisions.includes(routine.division))
            : [];
          
          // Create sections for both columns
          if (column1Routines.length > 0) {
            const column1Title = `${ageGroup} ${awardDisplayName} Winners - ${this.getSplitColumnLabel(column1Divisions)}`;
            createSection(column1Routines, column1Title, `winners-age-${ageGroup}-col1`);
          }
          
          if (column2Routines.length > 0) {
            const column2Title = `${ageGroup} ${awardDisplayName} Winners - ${this.getSplitColumnLabel(column2Divisions)}`;
            createSection(column2Routines, column2Title, `winners-age-${ageGroup}-col2`);
          }
          
          // If no routines in either column but age group is split, show all as fallback
          if (column1Routines.length === 0 && column2Routines.length === 0 && ageGroupRoutines.length > 0) {
            createSection(ageGroupRoutines, `${ageGroup} ${awardDisplayName} Winners`, `winners-age-${ageGroup}-fallback`);
          }
        } else {
          // Not split - show all routines for this age group
          createSection(ageGroupRoutines, `${ageGroup} ${awardDisplayName} Winners`, `winners-age-${ageGroup}`);
        }
      });

      return sections;
    } else {
      // Single section for other award types (Studio Standouts, etc.)
      // Sort by studio name (alphabetically), then by score (highest to lowest)
      const sortedRoutines = [...routines].sort((a, b) => {
        // First compare studio names alphabetically
        const studioA = (a.studio || '').toLowerCase();
        const studioB = (b.studio || '').toLowerCase();
        if (studioA !== studioB) {
          return studioA.localeCompare(studioB);
        }
        
        // If same studio, compare final scores (highest to lowest)
        if (a.finalScore !== b.finalScore) {
          return b.finalScore - a.finalScore;
        }
        
        // Tie-breaker with drop scores
        const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                          a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
        const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                          b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
        
        return bDropScore - aDropScore;
      });

      return [{
        id: 'winners-main',
        title: `${awardDisplayName} Winners`,
        routines: sortedRoutines,
        columns: columns,
        showHeader: true,
        headerClassName: 'section-header-winners'
      }];
    }
  }

  // Get column definitions for winners (no placement column)
  private static getWinnersColumnDefinitions(reportType: string): PrintColumn[] {
    const baseColumns: PrintColumn[] = [
      {
        key: 'studioName',
        label: 'Studio Name',
        width: '200px',
        formatter: defaultColumnFormatters.studioName
      },
      {
        key: 'routineName', 
        label: 'Routine',
        width: '250px',
        formatter: defaultColumnFormatters.routineName
      },
      {
        key: 'score',
        label: 'Score',
        width: '150px',
        formatter: defaultColumnFormatters.score
      },
      {
        key: 'ageGroup',
        label: 'Age Division', 
        width: '120px',
        formatter: defaultColumnFormatters.ageGroup
      },
      {
        key: 'category',
        label: 'Category',
        width: '100px', 
        formatter: defaultColumnFormatters.category
      },
      {
        key: 'division',
        label: 'Performance Division',
        width: '150px',
        formatter: defaultColumnFormatters.division
      }
    ];

    return baseColumns;
  }

  // Main factory method
  static createLayout(
    reportType: string, 
    routines: AwardedRoutine[], 
    customAwards: any[] = [],
    isWinnersMode: boolean = false
  ): PrintSection[] {
    // If in winners mode, use winners-specific layout
    if (isWinnersMode) {
      return this.createWinnersLayout(routines, customAwards, reportType);
    }

    if (reportType === 'adjudication') {
      return this.createAdjudicationLayout(routines);
    }
    
    if (reportType === 'highScoreByAge' || reportType.startsWith('highScoreByAge')) {
      return this.createHighScoreByAgeLayout(routines, reportType);
    }
    
    if (reportType === 'highScoreByDivision' || reportType.startsWith('highScoreByDivision')) {
      return this.createHighScoreByDivisionLayout(routines, reportType);
    }
    
    if (reportType === 'greatestHits' || reportType.startsWith('greatestHits')) {
      return this.createGreatestHitsLayout(routines, customAwards, reportType);
    }
    
    if (reportType === 'studioStandout' || reportType.startsWith('studioStandout')) {
      return this.createStudioStandoutLayout(routines, customAwards, reportType);
    }

    // Fallback to adjudication layout
    return this.createAdjudicationLayout(routines);
  }
}

export default PrintLayoutFactory;
